import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileText, 
  Camera, 
  MessageSquare, 
  ClipboardList, 
  Calendar, 
  Clock, 
  User, 
  Building2,
  AlertTriangle,
  MapPin,
  Phone,
  ExternalLink,
  Package
} from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { OrdenTrabajoDocumentos } from './OrdenTrabajoDocumentos';
import { OrdenTrabajoMateriales } from './OrdenTrabajoMateriales';
import { DocumentacionIndicator } from './DocumentacionIndicator';
import { useNavigate } from 'react-router-dom';
import { JornadaForm } from '@/components/jornadas/JornadaForm';
import { useObrasContext } from '@/contexts/ObrasContext';
import { useAuth } from '@/contexts/AuthContext';


import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface OrdenTrabajoVistaDetalleProps {
  orden: OrdenTrabajo;
  isOpen: boolean;
  onClose: () => void;
}

const prioridadColors: Record<string, string> = {
  baja: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  media: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
  alta: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  urgente: 'bg-red-500/20 text-red-400 border-red-500/50',
};

const estadoColors: Record<string, string> = {
  creada: 'bg-slate-500/20 text-slate-400',
  asignada: 'bg-blue-500/20 text-blue-400',
  en_progreso: 'bg-purple-500/20 text-purple-400',
  finalizada: 'bg-emerald-500/20 text-emerald-400',
  cancelada: 'bg-red-500/20 text-red-400',
};

const prioridadLabels: Record<string, string> = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  urgente: 'Urgente',
};

const estadoLabels: Record<string, string> = {
  creada: 'Creada',
  asignada: 'Asignada',
  en_progreso: 'En Progreso',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
};

export const OrdenTrabajoVistaDetalle: React.FC<OrdenTrabajoVistaDetalleProps> = ({
  orden,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { obras } = useObrasContext();
  const { perfil } = useAuth();
  // El técnico no debe ver información económica ni referencias a Holded
  const puedeVerEconomico = ['admin', 'gestor', 'super_admin'].includes(perfil?.rol || '');
  const [activeTab, setActiveTab] = useState('detalles');

  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedActions, setSelectedActions] = useState<{ parte: boolean; jornada: boolean }>({
    parte: true,
    jornada: false,
  });
  const [showJornadaForm, setShowJornadaForm] = useState(false);

  const obrasActivas = obras.filter(obra => obra.estado === 'activa');

  const getFechaStatus = () => {
    if (!orden.fecha_limite) return null;
    const fecha = new Date(orden.fecha_limite);
    if (isPast(fecha) && !isToday(fecha)) {
      return { text: 'Vencida', className: 'text-red-400', icon: AlertTriangle };
    }
    if (isToday(fecha)) {
      return { text: 'Hoy', className: 'text-yellow-400', icon: AlertTriangle };
    }
    return null;
  };

  const fechaStatus = getFechaStatus();

  const handleDocumentarTrabajo = () => {
    setShowActionDialog(true);
  };

  const handleContinue = () => {
    setShowActionDialog(false);
    
    if (selectedActions.parte && selectedActions.jornada) {
      // First navigate to create parte, then show jornada form
      navigate(`/partes-trabajo?orden_id=${orden.id}&cliente_id=${orden.cliente_id}`);
      // TODO: Could use sessionStorage to trigger jornada form after parte creation
    } else if (selectedActions.parte) {
      navigate(`/partes-trabajo?orden_id=${orden.id}&cliente_id=${orden.cliente_id}`);
      onClose();
    } else if (selectedActions.jornada) {
      setShowJornadaForm(true);
    }
  };

  const handleJornadaSuccess = () => {
    setShowJornadaForm(false);
    onClose();
  };

  if (showJornadaForm) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="h-[100dvh] sm:h-[90vh] w-[100vw] sm:w-auto max-w-2xl p-4 sm:p-6 gap-3 flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Registrar Jornada</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0">
            <JornadaForm
              obrasActivas={obrasActivas}
              defaultOrdenTrabajoId={orden.id}
              defaultClienteId={orden.cliente_id}
              ordenTrabajoInfo={{
                id: orden.id,
                numero_ot: orden.numero_ot,
                titulo: orden.titulo,
                cliente_nombre: orden.cliente_nombre
              }}
              onSuccess={handleJornadaSuccess}
              onCancel={() => setShowJornadaForm(false)}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[100dvh] sm:h-[90vh] w-[100vw] sm:w-auto max-w-2xl p-4 sm:p-6 gap-3 flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-sm text-muted-foreground">{orden.numero_ot}</span>
                <Badge variant="outline" className={prioridadColors[orden.prioridad]}>
                  {prioridadLabels[orden.prioridad]}
                </Badge>
                <Badge className={estadoColors[orden.estado]}>
                  {estadoLabels[orden.estado]}
                </Badge>
              </div>
              <DialogTitle className="text-xl">{orden.titulo}</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <TabsList className={`grid w-full ${puedeVerEconomico ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <TabsTrigger value="detalles" className="gap-2">
              <FileText className="h-4 w-4" />
              Detalles
            </TabsTrigger>
            <TabsTrigger value="materiales" className="gap-2">
              <Package className="h-4 w-4" />
              Materiales
            </TabsTrigger>
            <TabsTrigger value="documentos" className="gap-2">
              <Camera className="h-4 w-4" />
              Fotos
            </TabsTrigger>
            {puedeVerEconomico && (
              <TabsTrigger value="notas" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Notas
              </TabsTrigger>
            )}
          </TabsList>


          <ScrollArea className="flex-1 min-h-0 mt-4 pr-2">
            <TabsContent value="detalles" className="mt-0 space-y-4">
              {/* Estado de documentación */}
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <FileText className="h-4 w-4" />
                    Documentación
                  </div>
                  <DocumentacionIndicator
                    partesCount={orden.partes_count || 0}
                    jornadasCount={orden.jornadas_count || 0}
                    estado={orden.estado}
                    variant="full"
                  />
                </CardContent>
              </Card>

              {/* Cliente - Información expandida */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    Cliente
                  </div>
                  <p className="font-medium text-lg">{orden.cliente?.nombre || orden.cliente_nombre || 'Cliente no especificado'}</p>
                  
                  {/* Dirección de instalación con enlace a Google Maps */}
                  {(orden.cliente?.direccion || orden.cliente?.poblacion) && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Dirección de Instalación</p>
                        {orden.cliente.direccion && <p>{orden.cliente.direccion}</p>}
                        {orden.cliente.poblacion && (
                          <p className="text-muted-foreground">
                            {orden.cliente.codigo_postal && `${orden.cliente.codigo_postal} `}{orden.cliente.poblacion}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 gap-2 w-full sm:w-auto"
                          asChild
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              [orden.cliente.direccion, orden.cliente.codigo_postal, orden.cliente.poblacion]
                                .filter(Boolean)
                                .join(', ')
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <MapPin className="h-4 w-4" />
                            Abrir en Google Maps
                          </a>
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Horario */}
                  {(orden.cliente?.horario_apertura || orden.cliente?.horario_cierre) && (
                    <div className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded-md">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>
                        Horario: {orden.cliente.horario_apertura || '?'} - {orden.cliente.horario_cierre || '?'}
                      </span>
                    </div>
                  )}
                  
                  {/* Teléfono */}
                  {orden.cliente?.telefono && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <a 
                        href={`tel:${orden.cliente.telefono}`} 
                        className="text-primary hover:underline"
                      >
                        {orden.cliente.telefono}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fecha y hora */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Calendar className="h-4 w-4" />
                      Fecha límite
                    </div>
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${fechaStatus?.className || ''}`}>
                        {orden.fecha_limite 
                          ? format(new Date(orden.fecha_limite), "d 'de' MMMM, yyyy", { locale: es })
                          : 'Sin fecha'}
                      </p>
                      {fechaStatus && (
                        <Badge variant="outline" className={fechaStatus.className}>
                          {fechaStatus.text}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Clock className="h-4 w-4" />
                      Hora de ejecución
                    </div>
                    <p className="font-medium">
                      {orden.hora_ejecucion ? orden.hora_ejecucion.slice(0, 5) : 'Sin hora asignada'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Duración estimada */}
              {orden.duracion_estimada && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Clock className="h-4 w-4" />
                      Duración estimada
                    </div>
                    <p className="font-medium">{orden.duracion_estimada}</p>
                  </CardContent>
                </Card>
              )}

              {/* Descripción */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <FileText className="h-4 w-4" />
                    Descripción
                  </div>
                  <p className="text-sm whitespace-pre-wrap">
                    {orden.descripcion || 'Sin descripción'}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="materiales" className="mt-0">
              <OrdenTrabajoMateriales
                ordenTrabajoId={orden.id}
                empresaId={(orden as any).empresa_id}
                clienteId={orden.cliente_id}
              />
            </TabsContent>

            <TabsContent value="documentos" className="mt-0">
              <OrdenTrabajoDocumentos ordenTrabajoId={orden.id} />
            </TabsContent>

            {puedeVerEconomico && (
              <TabsContent value="notas" className="mt-0 space-y-4">
                {orden.notas_admin && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <User className="h-4 w-4" />
                        Notas del administrador
                      </div>
                      <p className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                        {orden.notas_admin}
                      </p>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Las notas adicionales se pueden añadir al crear el parte de trabajo
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

          </ScrollArea>
        </Tabs>

        {/* Footer with action buttons */}
        <div className="flex-shrink-0 pt-4 border-t pb-[env(safe-area-inset-bottom)]">
          <Button 
            onClick={handleDocumentarTrabajo} 
            className="w-full gap-2"
            size="lg"
          >
            <ClipboardList className="h-5 w-5" />
            Documentar Trabajo
          </Button>
        </div>

        {/* Action selection dialog */}
        <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                ¿Cómo quieres documentar este trabajo?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div 
                className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedActions.parte ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSelectedActions(prev => ({ ...prev, parte: !prev.parte }))}
              >
                <Checkbox 
                  checked={selectedActions.parte}
                  onCheckedChange={(checked) => setSelectedActions(prev => ({ ...prev, parte: !!checked }))}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium cursor-pointer">
                    Crear Parte de Trabajo
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Documento con firma del cliente para facturación
                  </p>
                </div>
              </div>

              <div 
                className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedActions.jornada ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setSelectedActions(prev => ({ ...prev, jornada: !prev.jornada }))}
              >
                <Checkbox 
                  checked={selectedActions.jornada}
                  onCheckedChange={(checked) => setSelectedActions(prev => ({ ...prev, jornada: !!checked }))}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium cursor-pointer">
                    Registrar Jornada
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Solo registro de horas (control interno)
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowActionDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleContinue}
                disabled={!selectedActions.parte && !selectedActions.jornada}
              >
                Continuar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};